import clsx from "clsx";
import {
  ArrowDownAz,
  Calendar,
  EllipsisVertical,
  LucideIcon,
  Merge,
  Pen,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { trackEvent } from "~/utils/analytics";
import { CategoryItem } from "../../types";

interface CategoryActionsProps {
  className?: string;
  handleOpenChange: (isOpen: boolean) => void;
  handleRename: () => void;
  handleDelete: () => void;
  handleMergeAll: () => void;
  categories: CategoryItem[];
  handleSortByName: () => void;
  handleSortByDate: () => void;
  canBeSorted: boolean;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
}

export const CategoryActions: FC<CategoryActionsProps> = ({
  className = "",
  handleOpenChange,
  handleRename,
  handleDelete,
  handleMergeAll,
  categories,
  handleSortByName,
  handleSortByDate,
  canBeSorted,
}) => {
  const [open, setOpen] = useState(false);

  const actionItems = [
    {
      id: "editName",
      icon: Pen,
      messageId: "docList.category.actionsDropdown.rename",
      defaultMessage: "Rename",
      action: handleRename,
    },
    canBeSorted && {
      id: "sortByName",
      icon: ArrowDownAz,
      messageId: "docList.category.actionsDropdown.sortByName",
      defaultMessage: "Sort by Name",
      action: handleSortByName,
    },
    canBeSorted && {
      id: "sortByDate",
      icon: Calendar,
      messageId: "docList.category.actionsDropdown.sortByDate",
      defaultMessage: "Sort by Date",
      action: handleSortByDate,
    },
    canBeSorted && {
      id: "mergeAll",
      icon: Merge,
      messageId: "docList.category.actionsDropdown.mergeAll",
      defaultMessage: "Merge All",
      action: handleMergeAll,
    },
    categories.length > 1 && {
      id: "delete",
      icon: Trash2,
      messageId: "docList.category.actionsDropdown.delete",
      defaultMessage: "Delete",
      action: handleDelete,
    },
  ].filter(Boolean) as ActionItem[];

  useEffect(() => {
    if (open) {
      trackEvent({ eventName: "app/doc_list/category_actions.button/click" });
    }
  }, [open]);

  return (
    <div onClick={(e) => e.stopPropagation()} className="w-full">
      <DropdownMenu
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          handleOpenChange(isOpen);
        }}
      >
        <DropdownMenuTrigger asChild>
          <EllipsisVertical className={clsx("w-4 h-4 text-white", className)} />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-34" align="end">
          {actionItems.map((item) => {
            const actionItem = item as ActionItem;
            return (
              <DropdownMenuItem
                key={actionItem.id}
                className={clsx(
                  "cursor-pointer whitespace-nowrap",
                  actionItem.className
                )}
                onClick={actionItem.action}
                destructive={actionItem.id === "delete"}
              >
                <actionItem.icon className="mr-2 h-4 w-4" />
                <FormattedMessage
                  id={actionItem.messageId}
                  defaultMessage={actionItem.defaultMessage}
                />
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
